import { useEffect, useState } from "react";
import { useSubmitLock } from "../../hooks/useSubmitLock";
import PropTypes from 'prop-types';
import { toast } from "react-hot-toast";
import {
  CheckIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  DownloadIcon,
  PenIcon,
} from "../../Icons";
import EvaluationInput from "../../pages/EvaluationNew/components/EvaluationInput";
import InputDatePicker from "../../pages/EvaluationNew/components/InputDatePicker";
import EvaluationSignatureModal from "../../pages/EvaluationNew/components/EvaluationSignatureModal";

import Button from "../../components/UI/Button";
import { canUserSign, isOfficeReviewerUser, isSurveyorUser } from "../../utils/formPermissions";
import useCurrentUser from "../../hooks/useCurrentUser";


const ApplicationFormFooter = ({
  page,
  setPage,
  totalPages = 2, // Default to 2 based on previous context, but can be overridden
  methods, // RHF methods
  onSave, // Function to call when "Save" is clicked (flag: 'save')
  onSubmit, // Function to call when "Send to Office" is clicked (flag: 'send_to_office')
  onNext, // Optional: function to handle "Next" click (e.g., validation)
  // Signature Props
  isSignatureModalOpen,
  openSignatureModal,
  closeSignatureModal,
  handleSignatureApply,
  signerName,
  // Form values for signatures (user can pass these or we can watch them)
  applicantName,
  applicantSignature,
  reviewedBySignedBy,
  reviewedBySignature,
  reviewedBySignedAt,
  // Configuration Props
  showCertification = true, // Default to true for Applications
  requireSignatures = true, // Default to true, set to false to skip signature validation
  requireSignaturesOnSave = true,
  showSignatureSections = true, // Default to true, set to false to hide employee/admin signature sections
  treatOaAsAdmin = false, // Daily Log: OA uses the same office buttons/signature as Admin
  onStatusUpdate = () => { }, // Function to handle status updates (completed, to_be_corrected)
  isReadOnly = false, // If true, disable all buttons and interactions
  applicantSignatureDateField = "applicantSignedAt",
  adminSignatureDateField = "reviewedBySignedAt",
  currentStatus = "",
  skipValidation = false
}) => {

  // Helper to determine if we are on the last page
  const isLastPage = page === totalPages;
  const [currentUser, setCurrentUser] = useState(null);
  const [showCommentBox, setShowCommentBox] = useState(false);
  const [comments, setComments] = useState("");
  const isSurveyRole = isSurveyorUser(currentUser);
  const { isSubmitting, pendingAction, runLocked } = useSubmitLock();
  const actionsDisabled = isReadOnly || isSurveyRole || isSubmitting;

  // Fetch user from local storage
  useEffect(() => {
    try {
      const userStr = localStorage.getItem("currentUser");
      if (userStr) {
        setCurrentUser(JSON.parse(userStr));
      }
    } catch (error) {
      console.error("Error parsing currentUser", error);
    }
  }, []);

  // Use centralized permission logic
  const isOfficeReviewer = treatOaAsAdmin
    ? isOfficeReviewerUser(currentUser)
    : canUserSign(currentUser, 'admin');
  const canSignEmployee = isOfficeReviewer
    ? false
    : canUserSign(currentUser, 'applicant');
  const canSignAdmin = isOfficeReviewer;
  const isAdmin = isOfficeReviewer; // For internal usage in this component

  const watchedApplicantDate = methods.watch(applicantSignatureDateField);
  const watchedAdminDate = methods.watch(adminSignatureDateField);
  const { errors: formErrors } = methods.formState;

  const handleNext = async () => {
    if (onNext) {
      await onNext();
    } else {
      setPage((p) => Math.min(totalPages, p + 1));
    }
  };

  const validateSignatureFields = () => {
    if (!requireSignatures || !showSignatureSections || skipValidation) {
      return true;
    }

    methods.clearErrors([
      "applicantSignature",
      applicantSignatureDateField,
      "reviewedBySignature",
      adminSignatureDateField,
    ]);

    const employeeDate = methods.getValues(applicantSignatureDateField);
    const adminDate = methods.getValues(adminSignatureDateField);
    const hasEmployeeDate =
      employeeDate != null &&
      String(employeeDate).trim() !== "";
    const hasAdminDate =
      adminDate != null && String(adminDate).trim() !== "";

    let valid = true;

    if (canSignEmployee) {
      if (!applicantSignature) {
        methods.setError("applicantSignature", {
          type: "manual",
          message: "Employee signature is required.",
        });
        valid = false;
      }
      if (!hasEmployeeDate) {
        methods.setError(applicantSignatureDateField, {
          type: "manual",
          message: "Employee signature date is required.",
        });
        valid = false;
      }
    }

    if (canSignAdmin) {
      if (!reviewedBySignature) {
        methods.setError("reviewedBySignature", {
          type: "manual",
          message: "Admin signature is required.",
        });
        valid = false;
      }
      if (!hasAdminDate) {
        methods.setError(adminSignatureDateField, {
          type: "manual",
          message: "Admin signature date is required.",
        });
        valid = false;
      }
    }

    return valid;
  };

  useEffect(() => {
    if (applicantSignature) {
      methods.clearErrors("applicantSignature");
    }
  }, [applicantSignature, methods]);

  useEffect(() => {
    if (watchedApplicantDate != null && String(watchedApplicantDate).trim() !== "") {
      methods.clearErrors(applicantSignatureDateField);
    }
  }, [watchedApplicantDate, applicantSignatureDateField, methods]);

  useEffect(() => {
    if (reviewedBySignature) {
      methods.clearErrors("reviewedBySignature");
    }
  }, [reviewedBySignature, methods]);

  useEffect(() => {
    if (watchedAdminDate != null && String(watchedAdminDate).trim() !== "") {
      methods.clearErrors(adminSignatureDateField);
    }
  }, [watchedAdminDate, adminSignatureDateField, methods]);

  const handleAction = async (type, status) => {
    if (actionsDisabled) return;

    const data = methods.getValues();
    const processAction = async (formData) => {
      const needSignatures = type !== "save" || requireSignaturesOnSave;
      if (needSignatures && !validateSignatureFields()) {
        // Same message as handleSubmit's onInvalid — resolver passed, so signature/date errors are manual
        toast.error("Please fix errors before proceeding.");
        return;
      }

      const payload = { ...formData };

      if (!isAdmin) {
        if (type === 'save' && currentStatus === 'to_be_corrected') {
          return await onSubmit(payload, "to_be_corrected")
        } else if (type === 'save' || type === 'send_to_office') {
          return await onSubmit(payload, type);
        }
      }

      if (type === 'save' || status === 'pending') {
        if (onSave) await onSave(payload, 'save');
      } else if (type === 'send_to_office') {
        if (onSubmit) await onSubmit(payload, 'send_to_office');
      } else {
        await onSubmit(payload, status);
      }
    };

    await runLocked(type, async () => {
      if (skipValidation) {
        await processAction(data);
      } else {
        await methods.handleSubmit(processAction, () => {
          toast.error("Please fix errors before proceeding.");
        })();
      }
    });
  };

  const handleComplete = async () => {
    if (actionsDisabled) return;

    methods.clearErrors(["reviewedBySignature", adminSignatureDateField]);
    const adminDate = methods.getValues(adminSignatureDateField);
    const hasAdminDate = adminDate != null && String(adminDate).trim() !== "";
    let ok = true;
    if (!reviewedBySignature) {
      methods.setError("reviewedBySignature", {
        type: "manual",
        message: "Admin signature is required to complete the application.",
      });
      ok = false;
    }
    if (!hasAdminDate) {
      methods.setError(adminSignatureDateField, {
        type: "manual",
        message: "Admin signature date is required.",
      });
      ok = false;
    }
    if (!ok) {
      toast.error("Please fix errors before proceeding.");
      return;
    }

    await runLocked("completed", async () => {
      if (onStatusUpdate) {
        await onStatusUpdate("completed", {
          reviewedBySignature,
          reviewedBySignedAt,
          reviewedBySignedBy
        });
      }
    });
  };

  const handleRequestChanges = () => {
    if (actionsDisabled) return;
    setShowCommentBox(true);
  };

  const submitRequestChanges = async () => {
    if (actionsDisabled) return;
    if (!comments || comments.trim() === "") {
      toast.error("Comments are required when requesting changes.");
      return;
    }

    methods.clearErrors(["reviewedBySignature", adminSignatureDateField]);
    const adminDate = methods.getValues(adminSignatureDateField);
    const hasAdminDate = adminDate != null && String(adminDate).trim() !== "";
    let sigOk = true;
    if (!reviewedBySignature) {
      methods.setError("reviewedBySignature", {
        type: "manual",
        message: "Admin signature is required.",
      });
      sigOk = false;
    }
    if (!hasAdminDate) {
      methods.setError(adminSignatureDateField, {
        type: "manual",
        message: "Admin signature date is required.",
      });
      sigOk = false;
    }
    if (!sigOk) {
      toast.error("Please fix errors before proceeding.");
      return;
    }

    await runLocked("to_be_corrected", async () => {
      if (onStatusUpdate) {
        await onStatusUpdate("to_be_corrected", { comments });
        setShowCommentBox(false);
        setComments("");
      }
    });
  };

  const { companyName } = useCurrentUser() || {};

  return (
    <div className="mt-12">
      {/* Certification & Signatures - Only display on Last Page */}
      {isLastPage && (
        <>
          {/* Certification Section */}
          {showCertification && (
            <div className="mb-8 p-6 border border-gray-200 rounded-xl bg-blue-50/50">
              <h4 className="font-bold text-gray-800 text-sm mb-3 uppercase tracking-wide flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-blue-600 rounded-full"></span>
                Applicant Certification
              </h4>
              <p className="font-medium text-gray-600 text-xs mb-4 uppercase tracking-wide">
                DO NOT SIGN UNTIL YOU HAVE READ THE ABOVE APPLICANT STATEMENT.
              </p>

              <div className="flex items-start gap-3 bg-white p-4 rounded-lg border border-gray-200 hover:border-blue-300 transition-colors shadow-sm">
                <input
                  type="checkbox"
                  {...methods.register("applicantStatementAccepted", {
                    required:
                      "You must certify that you have read and accepted the terms.",
                  })}
                  disabled={isReadOnly || isSurveyRole}
                  className={`mt-1 h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded ${isReadOnly || isSurveyRole ? "cursor-not-allowed opacity-70" : "cursor-pointer"
                    }`}
                />
                <label className="text-sm text-gray-700 leading-relaxed pt-0.5">
                  I certify that I have read, fully understand and accept all
                  terms of the foregoing Applicant Statement.{" "}
                  <span className="text-red-500 font-bold">*</span>
                </label>
              </div>
              {methods.formState?.errors?.applicantStatementAccepted && (
                <p className="text-red-500 text-sm mt-2 font-medium flex items-center gap-1">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                  {methods.formState.errors.applicantStatementAccepted.message}
                </p>
              )}
            </div>
          )}

          {/* Signature Sections */}

          {showSignatureSections && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8 border-t border-gray-200 pt-8">
              {/* Helper to extract name from signature string (Name Date) */}
              {(() => {
                const extractName = (nameField, signatureField) => {
                  if (nameField && typeof nameField === 'string' && nameField.trim() !== "") {
                    const dateRegex = /\d{1,2}\/\d{1,2}\/\d{4}/;
                    if (dateRegex.test(nameField)) {
                      return nameField.split(dateRegex)[0].trim();
                    }
                    return nameField;
                  }
                  if (signatureField && typeof signatureField === 'string') {
                    const dateRegex = /\d{1,2}\/\d{1,2}\/\d{4}/;
                    return signatureField.split(dateRegex)[0].trim();
                  }
                  return "";
                };



                const currentUserFullName = currentUser ? `${currentUser.firstName || ""} ${currentUser.lastName || ""}`.trim() || currentUser.name || "" : "";

                // Employee Full Name is the person the form belongs to — saved field first.
                // Never substitute the logged-in admin/reviewer into this box.
                const savedEmployeeName = extractName(applicantName, applicantSignature);
                const displayedEmployeeName = savedEmployeeName
                  || ((!isAdmin && !isSurveyRole) ? currentUserFullName : "");

                const displayedAdminName = isAdmin && currentUserFullName
                  ? currentUserFullName
                  :
                   (extractName(reviewedBySignedBy, reviewedBySignature) || (isAdmin ? signerName : ""));

                return (
                  <>
                    {/* Employee Section */}
                    <div className="flex flex-col h-full bg-white p-6 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                      <h3 className="text-lg font-bold text-gray-900 mb-6 pb-3 border-b border-gray-100 flex items-center gap-2">
                        <span className="bg-gray-100 p-1.5 rounded-lg text-gray-600">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-5 w-5"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </span>
                        Employee Section</h3>
                      <div className="space-y-5">
                        <div>
                          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
                            Employee Full Name
                          </label>
                          <div className={`p-3 bg-gray-50 border border-gray-200 rounded-lg font-medium ${!displayedEmployeeName ? 'text-gray-400 italic' : 'text-gray-800'}`}>
                            {displayedEmployeeName || "Pending Name..."}
                          </div>
                        </div>

                        <div>
                          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
                            Employee Signature
                          </label>
                          {applicantSignature ? (
                            <div className={`p-3 bg-gray-50 border rounded-lg text-gray-800 font-medium ${formErrors.applicantSignature ? "border-red-500" : "border-gray-200"}`}>
                              <p className="font-script">
                                {applicantSignature}
                              </p>
                            </div>
                          ) : canSignEmployee ? (
                            <button
                              type="button"
                              onClick={() => openSignatureModal("applicant")}
                              className={`w-full group px-4 py-3 bg-white text-blue-600 rounded-lg border-2 border-dashed transition-all duration-300 flex items-center justify-center gap-2 ${isReadOnly || isSurveyRole
                                ? "opacity-50 grayscale border-gray-200 cursor-not-allowed"
                                : formErrors.applicantSignature
                                  ? "border-red-500 border-solid hover:border-red-600 hover:bg-red-50"
                                  : "border-blue-300 hover:border-blue-500 hover:bg-blue-50"
                                }`}
                              disabled={isReadOnly || isSurveyRole}
                            >
                              <PenIcon className="h-5 w-5 group-hover:scale-110 transition-transform" />
                              <span className="font-medium text-sm">Click to Sign</span>
                            </button>
                          ) : (
                            <div className={`p-3 bg-gray-50 border rounded-lg min-h-[50px] text-gray-400 italic flex items-center ${formErrors.applicantSignature ? "border-red-500" : "border-gray-200"}`}>
                              Pending Signature
                            </div>
                          )}
                          {formErrors.applicantSignature && (
                            <p className="text-red-500 text-xs mt-1.5 font-medium">
                              {formErrors.applicantSignature.message}
                            </p>
                          )}
                        </div>

                        <div className="mt-auto">
                          <InputDatePicker
                            formatSeparator="/"
                            name={applicantSignatureDateField}
                            label="Date"
                            isRequired={canSignEmployee && requireSignatures}
                            disabled={isReadOnly || isSurveyRole || !canSignEmployee}
                          />
                        </div>
                      </div>
                    </div>


                    {/* Admin Section - Visible if isAdmin is true OR if admin has already signed OR if status is completed */}
                    {(isAdmin || reviewedBySignature || currentStatus === 'completed') && (
                      <div className="flex flex-col h-full bg-white p-6 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                        <h3 className="text-lg font-bold text-gray-900 mb-6 pb-3 border-b border-gray-100 flex items-center gap-2">
                          <span className="bg-gray-100 p-1.5 rounded-lg ">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-5 w-5"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                            >
                              <path
                                fillRule="evenodd"
                                d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </span>
                          Admin/Reviewer Section
                        </h3>
                        <div className="space-y-5">
                          <div>
                            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
                              Admin Full Name
                            </label>
                            <div className={`p-3 bg-gray-50 border border-gray-200 rounded-lg font-medium ${!displayedAdminName ? 'text-gray-400 italic' : 'text-gray-800'}`}>
                              {displayedAdminName || "Pending Name..."}
                            </div>
                          </div>

                          <div>
                            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
                              Admin Signature
                            </label>
                            {reviewedBySignature ? (
                              <div className={`p-3 bg-gray-50 border rounded-lg text-gray-800 font-medium ${formErrors.reviewedBySignature ? "border-red-500" : "border-gray-200"}`}>
                                <p className="font-script">
                                  {reviewedBySignature}
                                </p>
                              </div>
                            ) : (
                              canSignAdmin ? (
                                <button
                                  type="button"
                                  onClick={() => openSignatureModal("admin")}
                                  className={`w-full group mt-1 px-4 py-3 bg-white text-blue-600 rounded-lg border-2 border-dashed transition-all duration-300 flex items-center justify-center gap-2 ${isReadOnly || isSurveyRole
                                    ? "opacity-50 grayscale border-gray-200 cursor-not-allowed"
                                    : formErrors.reviewedBySignature
                                      ? "border-red-500 border-solid hover:border-red-600 hover:bg-red-50"
                                      : "border-blue-300 hover:border-blue-500 hover:bg-blue-50"
                                    }`}
                                  disabled={isReadOnly || isSurveyRole}
                                >
                                  <PenIcon className="h-5 w-5 group-hover:scale-110 transition-transform" />
                                  <span className="font-medium text-sm">Click to Sign Form</span>
                                </button>
                              ) : (
                                <div className={`p-3 bg-gray-50 border rounded-lg min-h-[50px] text-gray-400 italic flex items-center ${formErrors.reviewedBySignature ? "border-red-500" : "border-gray-200"}`}>
                                  Pending Signature
                                </div>
                              )
                            )}
                            {formErrors.reviewedBySignature && (
                              <p className="text-red-500 text-xs mt-1.5 font-medium">
                                {formErrors.reviewedBySignature.message}
                              </p>
                            )}
                          </div>

                          <div className="mt-auto">
                            <InputDatePicker
                              formatSeparator="/"
                              name={adminSignatureDateField}
                              label="Date"
                              isRequired={canSignAdmin && requireSignatures}
                              disabled={isReadOnly || isSurveyRole || !canSignAdmin}
                            />
                          </div>
                        </div>
                      </div>
                    )}

                  </>
                );
              })()}
            </div>
          )}
        </>
      )}

      {/* Navigation & Action Buttons */}
      <div className="flex flex-col sm:flex-row justify-between items-center gap-4 pt-8 border-t border-gray-100 relative">
        <button
          type="button"
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          disabled={page === 1}
          className="group px-6 py-3 rounded-xl font-semibold border transition-all duration-300 flex items-center gap-2 w-full sm:w-auto justify-center disabled:opacity-50 disabled:cursor-not-allowed border-gray-200 text-gray-600 bg-white hover:bg-gray-50 hover:border-gray-300 hover:text-gray-900 shadow-sm hover:shadow"
        >
          <ChevronLeftIcon className="h-5 w-5 group-hover:-translate-x-1 transition-transform" />
          Previous
        </button>

        <div className="flex items-center justify-center">
          <span className="text-sm font-bold text-gray-500 bg-gray-50 px-4 py-2 rounded-full border border-gray-200 shadow-sm min-w-[80px] text-center">
            {page} of {totalPages}
          </span>
        </div>

        {!isLastPage ? (
          <button
            type="button"
            onClick={handleNext}
            className="group px-8 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 hover:shadow-lg hover:shadow-blue-200 transition-all duration-300 font-semibold flex items-center gap-2 w-full sm:w-auto justify-center"
          >
            Next
            <ChevronRightIcon className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
          </button>
        ) : (
          (currentStatus !== 'completed') ? (
            <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
              {!isAdmin && (
                <>
                  <Button
                    text={pendingAction === "save" && isSubmitting ? "Saving…" : "Save"}
                    onClick={() => handleAction("save", currentStatus)}
                    icon={<DownloadIcon className="h-5 w-5 group-hover:-translate-y-1 transition-transform" />}
                    disabled={actionsDisabled}
                    loading={pendingAction === "save" && isSubmitting}
                  />
                  <Button
                    text={pendingAction === "send_to_office" && isSubmitting ? "Sending…" : "Sent to Office"}
                    onClick={() => handleAction("send_to_office", currentStatus)}
                    icon={<CheckIcon className="h-5 w-5 group-hover:scale-110 transition-transform" />}
                    disabled={actionsDisabled}
                    loading={pendingAction === "send_to_office" && isSubmitting}
                  />
                </>
              )}

              {isAdmin && (currentStatus !== 'completed') && (
                <>
                  <Button
                    text={pendingAction === "save" && isSubmitting ? "Saving…" : "Save"}
                    onClick={() => handleAction((currentStatus === 'pending' ? 'save' : (currentStatus || 'save')), currentStatus)}
                    icon={<CheckIcon className="h-5 w-5 group-hover:scale-110 transition-transform" />}
                    disabled={actionsDisabled}
                    loading={isSubmitting && pendingAction !== "completed" && pendingAction !== "to_be_corrected"}
                  />
                  <Button
                    text="To Be Corrected"
                    onClick={handleRequestChanges}
                    className="bg-yellow-500 hover:bg-yellow-600 text-white"
                    icon={<svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" /></svg>}
                    disabled={actionsDisabled}
                  />
                  <Button
                    text={pendingAction === "completed" && isSubmitting ? "Completing…" : "Completed"}
                    onClick={handleComplete}
                    variant="success"
                    icon={<CheckIcon className="h-5 w-5" />}
                    disabled={actionsDisabled}
                    loading={pendingAction === "completed" && isSubmitting}
                  />
                </>
              )}
            </div>
          ) : null
        )}
      </div>

      {/* Comment Box Modal/Overlay */}
      {showCommentBox && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg mx-4">
            <h3 className="text-lg font-bold mb-4">Request Changes</h3>
            <p className="text-sm text-gray-600 mb-4">
              Please provide details on what needs to be corrected.
            </p>
            <textarea
              className="w-full border border-gray-300 rounded-md p-3 h-32 mb-4 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter your comments here..."
              value={comments}
              onChange={(e) => setComments(e.target.value)}
            />
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowCommentBox(false)}
                disabled={isSubmitting}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submitRequestChanges}
                disabled={isSubmitting}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {pendingAction === "to_be_corrected" && isSubmitting
                  ? "Submitting…"
                  : "Submit Request"}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="mt-2">
        <div className="bg-gray-50 px-8 py-6 border-t border-gray-100 text-center">
          <p className="text-gray-500 text-sm font-medium">
            {companyName}
          </p>
        </div>
      </div>
      {/* Signature Modal */}
      <EvaluationSignatureModal
        isOpen={isSignatureModalOpen}
        onClose={closeSignatureModal}
        onSign={handleSignatureApply}
        disableSign={!signerName}
      >
        <div className="mb-6">
          <EvaluationInput
            label="Your Name"
            name="signerNameDisplay"
            value={signerName}
            readOnly={true}
            disabled={true}
            placeholder="Name will appear here"
          />
          <p className="text-xs text-gray-500 mt-1">
            Name is automatically retrieved from your login profile.
          </p>
        </div>
      </EvaluationSignatureModal>

    </div>
  );
};

export default ApplicationFormFooter;

ApplicationFormFooter.propTypes = {
  page: PropTypes.number.isRequired,
  setPage: PropTypes.func.isRequired,
  totalPages: PropTypes.number,
  methods: PropTypes.object.isRequired,
  onSave: PropTypes.func,
  onSubmit: PropTypes.func,
  onNext: PropTypes.func,
  isSignatureModalOpen: PropTypes.bool,
  openSignatureModal: PropTypes.func,
  closeSignatureModal: PropTypes.func,
  handleSignatureApply: PropTypes.func,
  signerName: PropTypes.string,
  applicantName: PropTypes.string,
  applicantSignature: PropTypes.string,
  applicantSignedAt: PropTypes.string,
  reviewedBySignedBy: PropTypes.string,
  reviewedBySignature: PropTypes.string,
  reviewedBySignedAt: PropTypes.string,
  showCertification: PropTypes.bool,
  requireSignatures: PropTypes.bool,
  showSignatureSections: PropTypes.bool,
  submitLabel: PropTypes.string,
  onStatusUpdate: PropTypes.func,
  isReadOnly: PropTypes.bool,
  onClose: PropTypes.func,
  applicantSignatureDateField: PropTypes.string,
  adminSignatureDateField: PropTypes.string,
  currentStatus: PropTypes.string,
  skipValidation: PropTypes.bool,
  requireSignaturesOnSave: PropTypes.bool,
  treatOaAsAdmin: PropTypes.bool,
};
